// src/api/adminApi.js
// Admin API calls (Orders, Users, Payments, Support, Settings).
// Long-term safe: endpoints that don't exist yet are provided as stubs that throw a clear error.

import axios from "axios";

/**
 * Backend base URL
 * - Uses REACT_APP_API_URL if set
 * - Otherwise defaults to local backend
 */
const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3001/api";

/**
 * Token storage key
 * If your app uses a different key, change it here.
 */

const api = axios.create({
  baseURL: API_BASE_URL,
});

// Attach JWT automatically (customer OR admin)
api.interceptors.request.use((config) => {
  const token =
    localStorage.getItem("adminToken") || // ✅ admin first
    localStorage.getItem("token");       // fallback

  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});



/**
 * Helper: Throw a clear error for endpoints you haven't implemented yet.
 * This is better than "commenting out" because:
 * - You keep your long-term architecture
 * - If you accidentally call it, you get a readable message
 */
function notImplemented(name, endpoint) {
  const err = new Error(`${name} is not implemented yet. Backend endpoint missing: ${endpoint}`);
  err.code = "NOT_IMPLEMENTED";
  throw err;
}

/* =====================================================
   ADMIN — METRICS
   ===================================================== */

export const getAdminMetrics = () => {
  return api.get("/admin/metrics");
};

/* =====================================================
   ADMIN — ORDERS
   ===================================================== */

// List orders with filters: status, q(search), customer_id, courier_id, limit, offset
export const getAdminOrders = (params = {}) => {
  return api.get("/admin/orders", { params });
};

// Get single order details (includes timeline + POD)
export const getAdminOrder = (orderId) => {
  return api.get(`/admin/orders/${orderId}`);
};

// Assign / reassign courier for an order
// ✅ FIX: backend expects { courier_id }, NOT { courierId }
export const reassignCourier = (orderId, courier_id) => {
  return api.post(`/admin/orders/${orderId}/assign`, { courier_id });
};

// Change order status (override)
export const setAdminOrderStatus = (orderId, nextStatus) => {
  return api.patch(`/admin/orders/${orderId}/status`, { nextStatus });
};

// Future endpoints (not in backend yet) — keep as safe stubs
export const cancelOrder = (_orderId, _reason) => {
  return notImplemented("cancelOrder", "POST /api/admin/orders/:id/cancel");
};

export const addAdminOrderNote = (_orderId, _note) => {
  return notImplemented("addAdminOrderNote", "POST /api/admin/orders/:id/notes");
};

/* =====================================================
   ADMIN — USERS
   ===================================================== */

export const getAdminCustomers = (params = {}) => {
  return api.get("/admin/users/customers", { params });
};

export const getAdminCouriers = (params = {}) => {
  return api.get("/admin/users/couriers", { params });
};

export const setUserSuspended = (userId, suspended, reason = "") => {
  return api.post(`/admin/users/${userId}/suspend`, { suspended, reason });
};

export const updateCourierVerification = (courierId, payload) => {
  return api.post(`/admin/users/couriers/${courierId}/verify`, payload);
};

/* =====================================================
   ADMIN — PAYMENTS / REFUNDS / PAYOUTS
   ===================================================== */

// Payments list (optionally filter by status)
export const getAdminPayments = (params = {}) => {
  return api.get("/admin/payments", { params });
};

// Refunds ledger list
export const getAdminRefunds = () => {
  return api.get("/admin/payments/refunds");
};

// Create refund record (manual allowed)
// ✅ Supports paymentId, method, external_ref (matches backend signature)
export const createRefund = ({
  orderId,
  paymentId = null,
  amount,
  reason = "",
  method = "manual",
  external_ref = null,
}) => {
  return api.post("/admin/payments/refunds", {
    orderId,
    paymentId,
    amount,
    reason,
    method,
    external_ref,
  });
};

// Payout ledger list
export const getAdminPayouts = () => {
  return api.get("/admin/payments/payouts");
};

// Create payout record
export const createPayout = ({
  order_id = null,
  courier_id = null,
  amount,
  method = "manual",
  notes = "",
}) => {
  return api.post("/admin/payments/payouts", {
    order_id,
    courier_id,
    amount,
    method,
    notes,
  });
};

// Mark payout as paid
export const markPayoutPaid = (payoutId, { method = "manual", notes = "" } = {}) => {
  return api.post(`/admin/payments/payouts/${payoutId}/paid`, { method, notes });
};

/* =====================================================
   ADMIN — SUPPORT / TICKETS / DISPUTES
   ===================================================== */

export const getAdminTickets = (params = {}) => {
  return api.get("/admin/support/tickets", { params });
};

export const createSupportTicket = ({
  order_id = null,
  customer_id = null,
  courier_id = null,
  subject,
  message,
  priority = "normal",
}) => {
  return api.post("/admin/support/tickets", {
    order_id,
    customer_id,
    courier_id,
    subject,
    message,
    priority,
  });
};

export const updateTicketStatus = (ticketId, status) => {
  return api.post(`/admin/support/tickets/${ticketId}`, { status });
};

export const getAdminDisputes = (params = {}) => {
  return api.get("/admin/support/disputes", { params });
};

export const resolveDispute = (disputeId, payload) => {
  return api.post(`/admin/support/disputes/${disputeId}/resolve`, payload);
};

/* =====================================================
   ADMIN — SETTINGS
   ===================================================== */

// List settings (optionally filter by category)
export const getAdminSettings = (params = {}) => {
  // Example: getAdminSettings({ category: "general" })
  return api.get("/admin/settings", { params });
};

// Bulk update settings
export const updateAdminSettings = (updates) => {
  // updates: [{ key, value, note? }]
  return api.post("/admin/settings", { updates });
};

// Audit history (optional)
export const getAdminSettingsAudit = (params = {}) => {
  // Example: getAdminSettingsAudit({ key: "maintenance_mode" })
  return api.get("/admin/settings/audit", { params });
};

